import styled from '@emotion/styled';
import { useToggle } from 'react-use';
import { useCallback } from 'react';
import { SortableHeadCell as KizenSortableHeadCell } from 'components/Kizen/Table';
import { DraggableCore } from 'react-draggable';
import Icon from 'components/Kizen/Icon';
import { gutters } from 'app/spacing';
import {
  getSizing,
  ResizerLayout,
} from 'pages/Common/components/BigTableCells';

const PADDING = 30; // Right cell padding in pixels
const DEFAULT_MIN_WIDTH = 200;

/**
 * Extracts minWidth from a basic column's headCell or cell props
 */
export const getBasicColumnMinWidth = (basicCol, fallback) => {
  return (
    basicCol?.headCell?.props?.minWidth ??
    basicCol?.cell?.props?.minWidth ??
    fallback
  );
};

export const parseMinWidth = (minWidth) => {
  return minWidth ? parseInt(minWidth, 10) : DEFAULT_MIN_WIDTH;
};

export const SortableHeadCell = styled(
  ({
    column,
    minWidth,
    children,
    className,
    onResizeColumnWidth,
    onResizeStop,
    getColumnWidth,
    narrowestWidth = 75,
    resizable = true,
    ...others
  }) => {
    const width = getColumnWidth(column.id);
    const [resizing, toggleResizing] = useToggle(false);
    const handleResize = useCallback(
      (ev, { x }) => {
        const nextWidth = Math.max(narrowestWidth, x - PADDING / 2);
        onResizeColumnWidth({
          id: column.id,
          label: column.label,
          width: `${nextWidth}px`,
          visible: true,
        });
      },
      [column.id, column.label, onResizeColumnWidth, narrowestWidth]
    );

    const onResizeColumnStop = useCallback(() => {
      onResizeStop();
    }, [onResizeStop]);

    return (
      <KizenSortableHeadCell
        className={`${className} ${resizing && 'Resizing'}`}
        column={column}
        padding={`${PADDING}px`}
        {...getSizing(width, minWidth)}
        {...others}
      >
        {children}
        {resizable ? (
          <ResizerLayout className="ResizerLayout" padding={PADDING}>
            <DraggableCore
              onDrag={handleResize}
              onStart={toggleResizing}
              onStop={() => {
                toggleResizing();
                onResizeColumnStop();
              }}
            >
              <Icon className="Resizer" icon="column-resize" size="intrinsic" />
            </DraggableCore>
          </ResizerLayout>
        ) : null}
      </KizenSortableHeadCell>
    );
  }
)`
  &.Resizing {
    cursor: ew-resize;
    svg line {
      stroke: var(--color-background-darker);
    }
  }
  > div {
    // Fixes small alignment issue with cell text
    line-height: 0;
    margin-top: 1px;
    button {
      margin-left: 10px;
    }
  }
  button {
    margin-left: ${gutters.spacing(1)}px;
  }
`;
